import { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { useStickyHeaderVar } from '../hooks/useStickyHeaderVar';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import { useTabs, markTabSwitchNav } from '../context/TabsContext';
import HeaderTabsBar from './HeaderTabsBar';
import { Button } from '../components/ui/button';
import { ScrollToTopButton } from './ScrollToTopButton';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { AIChatbot } from '../components/AIChatbot';
import GlobalCallOverlay from '../components/chat/GlobalCallOverlay';
import { MobileBalanceGate } from './MobileBalanceGate';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { AutoBreadcrumb } from '../components/ui/auto-breadcrumb';
import { BuildVersionBadge } from './BuildVersionBadge';
import { AppVersionInfo } from './AppVersionInfo';
import {
  Leaf,
  LayoutDashboard,
  FileText,
  Award,
  CreditCard,
  Users,
  Building2,
  Receipt,
  Car,
  BarChart3,
  TrendingUp,
  Settings,
  LogOut,
  Menu,
  ChevronDown,
  ChevronRight,
  Bell,
  User,
  Wallet,
  Banknote,
  BookOpen,
  Activity,
  DollarSign,
  Clock,
  Smartphone,
  ClipboardList,
  RefreshCcw,
  FileBarChart,
  UsersRound,
  Cog,
  ShieldCheck,
  Mail,
  MessageSquare,
  Check,
  Trash2,
  CheckCheck,
  AlertCircle,
  Info,
  CheckCircle2,
  PiggyBank,
  Palette,
  Headphones,
  Shield,
  Key,
  Phone,
  PhoneMissed,
  Video,
  ArrowUpRight,
  XCircle,
  Wrench,
  Megaphone,
  WifiOff,
  RefreshCw,
  Calendar,
  Calculator,
  UserPlus,
  Plus,
  Database,
  DatabaseBackup,
  AlertTriangle,
  Target,
  Search,
  Globe,
  Contact,
  MapPin,
  Inbox,
  HardDrive,
} from 'lucide-react';
import { useActionCount } from '../hooks/useActionCount';
import { cn, formatDate } from '../lib/utils';
import GlobalSearch from './GlobalSearch';
import WorkAreaLock from './WorkAreaLock';
import { HomeShortcutMenu } from './HomeShortcutMenu';
import { useHomeShortcut } from '../hooks/useHomeShortcut';

// Offline status hook
const useOfflineStatus = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [syncCount, setSyncCount] = useState(0);

  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => setIsOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    
    const onSyncComplete = (e) => {
      if (e.detail?.synced > 0) setSyncCount(e.detail.synced);
      setTimeout(() => setSyncCount(0), 3000);
    };
    window.addEventListener('background-sync-complete', onSyncComplete);
    
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
      window.removeEventListener('background-sync-complete', onSyncComplete);
    };
  }, []);

  return { isOffline, syncCount };
};

const OfflineBanner = () => {
  const { isOffline, syncCount } = useOfflineStatus();
  if (!isOffline && syncCount === 0) return null;
  
  if (syncCount > 0) {
    return (
      <div className="bg-green-600 text-white text-xs text-center py-1 px-2 flex items-center justify-center gap-1 shrink-0">
        <RefreshCw className="w-3 h-3" />
        <span>{syncCount} item{syncCount > 1 ? 's' : ''} synced</span>
      </div>
    );
  }

  return (
    <div className="bg-amber-600 text-white text-xs text-center py-1 px-2 flex items-center justify-center gap-1 shrink-0">
      <WifiOff className="w-3 h-3" />
      <span>You're offline. Changes will sync when reconnected.</span>
    </div>
  );
};

export const StaffSidebar = ({ className }) => {
  const location = useLocation();
  const { user, hasRole, token } = useAuth();
  const { count: actionCount, eligible: actionEligible } = useActionCount(user, token);
  
  // Get unread count and registerHandler from chat context
  let unreadTotal = 0;
  let chatRegisterHandler = null;
  try {
    const chatContext = useChat();
    unreadTotal = chatContext?.unreadTotal || 0;
    chatRegisterHandler = chatContext?.registerHandler;
  } catch (e) {
    // Chat context not available
  }
  
  // Single state to track which menu is open (accordion behavior)
  const [openMenu, setOpenMenu] = useState(null);
  // Opening a submenu page closes the group that was expanded to reach it
  useEffect(() => { setOpenMenu(null); }, [location.pathname]);

  // Toggle menu - closes others automatically
  const shortcut = useHomeShortcut();
  const toggleMenu = (menuName) => {
    setOpenMenu(openMenu === menuName ? null : menuName);
  };

  // Check if specific menu is open
  const loanMenuOpen = openMenu === 'loan';
  const reportsMenuOpen = openMenu === 'reports';
  const staffMenuOpen = openMenu === 'staff';
  const accountMenuOpen = openMenu === 'account';
  const systemMenuOpen = openMenu === 'system';
  const investorMenuOpen = openMenu === 'investor';
  const socialMenuOpen = openMenu === 'social';

  // Check if current path is under loan management
  const isLoanPath = ['/staff/applications', '/staff/loans', '/staff/loans-hub', '/staff/disbursed-loans', '/staff/loan-watchlist', '/staff/payments', '/admin/loan-settings', '/staff/fees', '/staff/rejected-loans', '/admin/fix-application-fees', '/staff/mass-action'].includes(location.pathname);
  
  // Check if current path is under reports
  const isReportsPath = ['/staff/reports', '/staff/reports/transactions', '/staff/activity-logs', '/staff/analytics'].some(
    path => location.pathname.startsWith(path)
  );
  
  // Check if current path is under staff management
  const isStaffPath = ['/staff/team', '/staff/recruitment', '/staff/my-attendance', '/staff/my-loan', '/staff/attendance', '/admin/staff-loans', '/staff/my-payslips', '/admin/payroll', '/admin/performance-reports', '/admin/staff-registrations', '/staff/staff-update-requests'].includes(location.pathname);
  
  // Check if current path is under account management
  const isAccountPath = ['/staff/expenses', '/staff/petty-cash', '/staff/expense-analytics', '/staff/income', '/staff/reports/profit-loss'].includes(location.pathname);
  
  // Check if current path is under system management
  const isSystemPath = ['/staff/settings', '/staff/branches', '/admin/settings', '/admin/scheduled-jobs', '/admin/scheduled-reports', '/admin/registration-settings', '/staff/chat-admin', '/admin/data-health', '/admin/message-footers'].includes(location.pathname);
  
  // Check if current path is under investor management
  const isInvestorPath = ['/admin/investors', '/admin/investment-products', '/admin/investments', '/admin/withdrawals', '/admin/people', '/admin/interest-payments', '/admin/investment-hub'].includes(location.pathname)
    || location.pathname.startsWith('/admin/people')
    || location.pathname.startsWith('/admin/investment-hub');

  // Check if current path is under social
  const isSocialPath = ['/staff/calendar', '/staff/chat', '/admin/chat-files', '/staff/support-queue', '/staff/social-contacts', '/staff/contacts', '/staff/key-contacts', '/staff/navigation'].includes(location.pathname);

  // Simple menu items (non-grouped)
  const simpleMenuItems = [
    { 
      icon: LayoutDashboard, 
      label: 'Dashboard', 
      href: '/staff/dashboard',
      roles: ['admin', 'regional_manager', 'branch_manager', 'loan_officer', 'cashier']
    },
  ];

  // Social submenu items
  const socialMenuItems = [
    { 
      icon: Calendar, 
      label: 'Calendar', 
      href: '/staff/calendar',
      roles: ['admin', 'regional_manager', 'branch_manager', 'loan_officer', 'cashier']
    },
    { 
      icon: MessageSquare, 
      label: 'Chat & Calls', 
      href: '/staff/chat',
      roles: ['admin', 'regional_manager', 'branch_manager', 'loan_officer', 'cashier', 'social']
    },
    { 
      icon: HardDrive, 
      label: 'Chat File Cleanup', 
      href: '/admin/chat-files',
      roles: ['admin']
    },
    { 
      icon: Users, 
      label: 'Support Queue', 
      href: '/staff/support-queue',
      roles: ['admin', 'regional_manager', 'branch_manager', 'loan_officer', 'cashier']
    },
    {
      icon: Globe,
      label: 'Contacts',
      href: '/staff/contacts',
      roles: ['admin', 'regional_manager', 'branch_manager', 'loan_officer', 'cashier', 'social']
    },
    {
      icon: Contact,
      label: 'Key Contacts',
      href: '/staff/key-contacts',
      roles: ['admin', 'regional_manager', 'branch_manager', 'loan_officer', 'cashier']
    },
    {
      icon: Globe,
      label: 'Social Requests',
      href: '/staff/social-contacts',
      roles: ['admin']
    },
    {
      icon: MapPin,
      label: 'Navigation',
      href: '/staff/navigation',
      roles: ['admin', 'regional_manager', 'branch_manager', 'loan_officer', 'cashier', 'social']
    },
  ];

  // Loan Management submenu items
  const loanMenuItems = [
    {
      icon: Wallet,
      label: 'Loans Hub', 
      href: '/staff/loans-hub',
      roles: ['admin', 'regional_manager', 'branch_manager', 'loan_officer', 'cashier']
    },
    {
      icon: Plus,
      label: 'Create Loan',
      href: '/staff/create-loan',
      roles: ['admin']
    },
    { 
      icon: Banknote, 
      label: 'Disbursed Loans', 
      href: '/staff/disbursed-loans',
      roles: ['admin', 'regional_manager', 'branch_manager', 'loan_officer', 'cashier']
    },
    { 
      icon: Target, 
      label: 'Loan Watchlist', 
      href: '/staff/loan-watchlist',
      roles: ['admin', 'regional_manager', 'branch_manager', 'loan_officer', 'cashier']
    },
    { 
      icon: Calculator, 
      label: 'Loan Calculator', 
      href: '/staff/loan-calculator',
      roles: ['admin', 'regional_manager', 'branch_manager', 'loan_officer', 'cashier']
    },
    { 
      icon: RefreshCcw, 
      label: 'Refunds', 
      href: '/staff/refunds',
      roles: ['admin', 'regional_manager', 'branch_manager', 'loan_officer', 'cashier']
    },
    {
      icon: AlertTriangle,
      label: 'Risk Watchlist',
      href: '/staff/risk-watchlist',
      roles: ['admin', 'regional_manager', 'branch_manager', 'loan_officer']
    },
    { 
      icon: Receipt, 
      label: 'Fee Management', 
      href: '/staff/fees',
      // User RBAC tightening (May 13 2026): hidden for Manager and below — was visible to branch_manager.
      roles: ['admin', 'regional_manager']
    },
    { 
      icon: Settings, 
      label: 'Loan Products', 
      href: '/admin/loan-settings',
      roles: ['admin', 'regional_manager']
    },
    { 
      icon: Wrench, 
      label: 'Fix App Fees', 
      href: '/admin/fix-application-fees',
      roles: ['admin', 'regional_manager']
    },
    { 
      icon: FileBarChart, 
      label: 'Mass Action', 
      href: '/staff/mass-action',
      // RBAC tightening: hidden for Manager and below — was visible to branch_manager and loan_officer.
      roles: ['admin', 'regional_manager']
    },
  ];

  // System Reports submenu items
  const reportsMenuItems = [
    { 
      icon: BarChart3, 
      label: 'Reports', 
      href: '/staff/reports',
      roles: ['admin', 'regional_manager', 'branch_manager', 'loan_officer']
    },
    { 
      icon: CreditCard, 
      label: 'Transaction Reports', 
      href: '/staff/reports/transactions',
      roles: ['admin', 'regional_manager', 'branch_manager', 'loan_officer', 'cashier']
    },
    { 
      icon: Activity, 
      label: 'Activity Logs', 
      href: '/staff/activity-logs',
      roles: ['admin', 'regional_manager']
    },
    { 
      icon: TrendingUp, 
      label: 'Analytics', 
      href: '/staff/analytics',
      roles: ['admin', 'regional_manager', 'branch_manager', 'loan_officer', 'cashier']
    },
    { 
      icon: BarChart3, 
      label: 'Monthly Statistics', 
      href: '/staff/reports/monthly-statistics',
      roles: ['admin', 'regional_manager', 'branch_manager', 'loan_officer', 'cashier']
    },
    { 
      icon: Megaphone, 
      label: 'Marketing', 
      href: '/staff/marketing',
      roles: ['admin', 'regional_manager', 'branch_manager', 'loan_officer', 'cashier']
    },
  ];

  // Staff Management submenu items
  const staffMenuItems = [
    { 
      icon: Users, 
      label: 'Staff', 
      href: '/staff/team',
      roles: ['admin', 'regional_manager', 'branch_manager']
    },
    { 
      icon: UserPlus, 
      label: 'Recruitment', 
      href: '/staff/recruitment',
      roles: ['admin', 'regional_manager'],
      taskRoles: ['recruitment']
    },
    { 
      icon: AlertTriangle, 
      label: 'System Alerts', 
      href: '/staff/system-alerts',
      roles: ['admin']
    },
    { 
      icon: Clock, 
      label: 'My Attendance', 
      href: '/staff/my-attendance',
      roles: ['regional_manager', 'branch_manager', 'loan_officer', 'cashier'],
      excludeRoles: ['admin'] // admin: moved into Attendance Register / Staff Loans / Payroll tabs
    },
    { 
      icon: Clock, 
      label: 'My Leave Request', 
      href: '/staff/my-leave',
      roles: ['admin', 'regional_manager', 'branch_manager', 'loan_officer', 'cashier']
    },
    { 
      icon: Smartphone, 
      label: 'Mobile Balances', 
      href: '/staff/mobile-balances',
      roles: ['admin', 'regional_manager', 'branch_manager']
    },
    { 
      icon: Banknote, 
      label: 'My Staff Loan', 
      href: '/staff/my-loan',
      roles: ['regional_manager', 'branch_manager', 'loan_officer', 'cashier'],
      excludeRoles: ['admin'] // admin: moved into Attendance Register / Staff Loans / Payroll tabs
    },
    { 
      icon: ClipboardList, 
      label: 'Attendance Register', 
      href: '/staff/attendance',
      roles: ['admin', 'regional_manager', 'branch_manager']
    },
    { 
      icon: Banknote, 
      label: 'Staff Loans', 
      href: '/admin/staff-loans',
      roles: ['admin']
    },
    { 
      icon: BookOpen, 
      label: 'General Ledger', 
      href: '/admin/general-ledger',
      roles: ['admin']
    },
    { 
      icon: FileText, 
      label: 'My Payslips', 
      href: '/staff/my-payslips',
      roles: ['regional_manager', 'branch_manager', 'loan_officer', 'cashier'],
      excludeRoles: ['admin'] // admin: moved into Attendance Register / Staff Loans / Payroll tabs
    },
    { 
      icon: FileText, 
      label: 'Payroll', 
      href: '/admin/payroll',
      // RBAC tightening: hidden for Manager and below.
      roles: ['admin', 'regional_manager']
    },
    { 
      icon: Award, 
      label: 'Performance Reports', 
      href: '/admin/performance-reports',
      roles: ['admin']
    },
    { 
      icon: Clock, 
      label: 'Loan Approval Delay', 
      href: '/staff/loan-approval-delay',
      roles: ['admin', 'regional_manager', 'branch_manager', 'relationship_manager']
    },
    { 
      icon: BookOpen, 
      label: 'Operation Manual', 
      href: '/staff/manual',
      roles: ['admin', 'regional_manager', 'branch_manager', 'relationship_manager', 'loan_officer', 'cashier']
    },
  ];

  // Account Management submenu items
  const accountMenuItems = [
    { 
      icon: Receipt, 
      label: 'Expenses', 
      href: '/staff/expenses',
      roles: ['admin', 'regional_manager', 'branch_manager', 'loan_officer', 'cashier']
    },
    { 
      icon: Car, 
      label: 'Transport Journal', 
      href: '/staff/transport-journal',
      roles: ['admin', 'regional_manager', 'branch_manager', 'loan_officer', 'cashier']
    },
    { 
      icon: Wallet, 
      label: 'Petty Cash', 
      href: '/staff/petty-cash',
      roles: ['admin', 'regional_manager', 'branch_manager']
    },
    { 
      icon: BarChart3, 
      label: 'Expense Analytics', 
      href: '/staff/expense-analytics',
      roles: ['admin', 'regional_manager', 'branch_manager']
    },
    { 
      icon: DollarSign, 
      label: 'Income', 
      href: '/staff/income',
      // RBAC tightening: hidden for RM and below — Admin-only.
      roles: ['admin']
    },
    { 
      icon: TrendingUp, 
      label: 'Profit & Loss', 
      href: '/staff/reports/profit-loss',
      // RBAC tightening: hidden for RM and below — Admin-only.
      roles: ['admin']
    },
  ];

  // System Management submenu items
  const systemMenuItems = [
    { 
      icon: Settings, 
      label: 'Settings', 
      href: '/staff/settings',
      roles: ['admin', 'regional_manager', 'branch_manager', 'loan_officer', 'cashier']
    },
    { 
      icon: Building2, 
      label: 'Branches', 
      href: '/staff/branches',
      roles: ['admin', 'regional_manager']
    },
    { 
      icon: Cog, 
      label: 'Admin Settings', 
      href: '/admin/settings',
      roles: ['admin']
    },
    { 
      icon: ShieldCheck, 
      label: 'Roles & Permissions', 
      href: '/staff/roles',
      roles: ['admin']
    },
    {
      icon: Smartphone,
      label: 'App Crash Reports',
      href: '/staff/app-crashes',
      roles: ['admin']
    },
    { 
      icon: Palette, 
      label: 'Appearance', 
      href: '/admin/appearance',
      roles: ['admin']
    },
    { 
      icon: Headphones, 
      label: 'Communication Roles', 
      href: '/admin/task-roles',
      roles: ['admin', 'regional_manager', 'branch_manager']
    },
    { 
      icon: Clock, 
      label: 'Scheduled Jobs', 
      href: '/admin/scheduled-jobs',
      roles: ['admin']
    },
    { 
      icon: Database, 
      label: 'Data Health', 
      href: '/admin/data-health',
      roles: ['admin']
    },
    { 
      icon: DatabaseBackup, 
      label: 'Backup & Restore', 
      href: '/admin/backups',
      roles: ['admin']
    },
    { 
      icon: Mail, 
      label: 'Email Reports', 
      href: '/admin/scheduled-reports',
      roles: ['admin']
    },
    { 
      icon: Mail, 
      label: 'Email Templates', 
      href: '/admin/email-templates',
      roles: ['admin']
    },
    { 
      icon: FileText, 
      label: 'Message Footers', 
      href: '/admin/message-footers',
      roles: ['admin']
    },
    { 
      icon: Shield, 
      label: 'Security', 
      href: '/admin/security',
      roles: ['admin']
    },
    { 
      icon: FileText, 
      label: 'Required Documents', 
      href: '/admin/required-documents',
      roles: ['admin']
    },
    { 
      icon: Settings, 
      label: 'Registration Settings', 
      href: '/admin/registration-settings',
      roles: ['admin']
    },
    { 
      icon: Key, 
      label: 'Active OTPs', 
      href: '/admin/active-otps',
      roles: ['admin', 'regional_manager']
    },
    { 
      icon: UserPlus, 
      label: 'Pending Registrations', 
      href: '/admin/pending-registrations',
      roles: ['admin', 'regional_manager']
    },
    {
      icon: Shield,
      label: 'Chat Administration',
      href: '/staff/chat-admin',
      roles: ['admin']
    },
  ];

  // Investor Management submenu items
  const investorMenuItems = [
    {
      icon: Users,
      label: 'Investors',
      href: '/admin/investors',
      roles: ['admin', 'regional_manager']
    },
    {
      icon: Wallet,
      label: 'Investment Hub',
      href: '/admin/investment-hub',
      roles: ['admin', 'regional_manager']
    },
    {
      icon: UserPlus,
      label: 'People',
      href: '/admin/people',
      roles: ['admin']
    },
  ];

  // Other menu items after loan management
  const otherMenuItems = [
    { 
      icon: Users, 
      label: 'Customers', 
      href: '/staff/customers',
      roles: ['admin', 'regional_manager', 'branch_manager', 'loan_officer', 'cashier']
    },
  ];

  const filterByRole = (items) => items.filter(item =>
    !(item.excludeRoles || []).includes(user?.role)
    && (item.roles.some(role => hasRole(role) || user?.role === role)
    || (item.taskRoles || []).some(tr => (user?.task_roles || []).includes(tr)))
  );

  const filteredSimpleItems = filterByRole(simpleMenuItems);
  const filteredSocialItems = filterByRole(socialMenuItems);
  const filteredLoanItems = filterByRole(loanMenuItems);
  const filteredReportsItems = filterByRole(reportsMenuItems);
  const filteredStaffItems = filterByRole(staffMenuItems);
  const filteredAccountItems = filterByRole(accountMenuItems);
  const filteredSystemItems = filterByRole(systemMenuItems);
  const filteredInvestorItems = filterByRole(investorMenuItems);
  const filteredOtherItems = filterByRole(otherMenuItems);

  // Check access
  const hasSocialAccess = filteredSocialItems.length > 0;
  const hasLoanAccess = filteredLoanItems.length > 0;
  const hasReportsAccess = filteredReportsItems.length > 0;
  const hasStaffAccess = filteredStaffItems.length > 0;
  const hasAccountAccess = filteredAccountItems.length > 0;
  const hasSystemAccess = filteredSystemItems.length > 0;
  const hasInvestorAccess = filteredInvestorItems.length > 0;
  return (
    <aside
      className={cn("w-64 bg-white border-r border-border flex flex-col", className)}
      onClickCapture={() => markTabSwitchNav()}
    >
      <div className="p-4 border-b border-border">
        <Link to="/staff/dashboard" className="flex items-center gap-2" data-testid="sidebar-logo">
          <img src="/bbc-logo.png" alt="BBC Logo" className="w-9 h-9 rounded-lg object-cover" />
          <div>
            <span className="text-lg font-heading font-bold" style={{ color: 'var(--theme-primary)' }}>BBC</span>
            <span className="block text-xs text-muted-foreground">Staff Portal</span>
          </div>
        </Link>
      </div>
      <ScrollArea className="flex-1 p-3">
        <nav className="space-y-0.5">
          {/* Simple menu items */}
          {filteredSimpleItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                location.pathname === item.href
                  ? "bg-forest-50 text-forest-900"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              <item.icon className="w-5 h-5" />
              <span className="flex-1">{item.label}</span>
            </Link>
          ))}

          {/* Action page — live inbox for BM / RM / Admin; the entry disappears (no gap) when nothing is pending */}
          {actionEligible && (actionCount > 0 || location.pathname === '/staff/actions') && (
            <Link
              to="/staff/actions"
              data-testid="nav-action-page"
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                location.pathname === '/staff/actions' ? "bg-forest-50 text-forest-900" : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              <Inbox className="w-5 h-5" />
              <span className="flex-1">Action page</span>
              <span className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center" data-testid="nav-action-count">{actionCount || 0}</span>
            </Link>
          )}

          {/* Social Menu Group */}
          {hasSocialAccess && (
            <div>
              <button
                onClick={() => toggleMenu('social')}
                onMouseDown={(e) => e.preventDefault()}
                {...shortcut.bind('Social', '/staff/chat')}
                data-testid="nav-social"
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  isSocialPath
                    ? "bg-forest-50 text-forest-900"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5" />
                  <span>Social</span>
                </div>
                <div className="flex items-center gap-2">
                  {unreadTotal > 0 && (
                    <Badge className="bg-red-500 text-white text-xs px-1.5 py-0.5 min-w-[20px] text-center">
                      {unreadTotal > 99 ? '99+' : unreadTotal}
                    </Badge>
                  )}
                  {socialMenuOpen ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </div>
              </button>
              
              {socialMenuOpen && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-forest-100 pl-3">
                  {filteredSocialItems.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setOpenMenu(null)}
                      {...shortcut.bind(item.label, item.href)}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                        location.pathname === item.href
                          ? "bg-forest-50 text-forest-900"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <item.icon className="w-4 h-4" />
                      <span className="flex-1">{item.label}</span>
                      {item.href === '/staff/chat' && unreadTotal > 0 && (
                        <Badge className="bg-red-500 text-white text-xs px-1.5 py-0.5 min-w-[20px] text-center" data-testid="chat-unread-badge">
                          {unreadTotal > 99 ? '99+' : unreadTotal}
                        </Badge>
                      )}
                    </Link>
                  ))}
                </div>
              )}
              <HomeShortcutMenu target={shortcut.target} onClose={shortcut.close} />
            </div>
          )}

          {/* Loan Management Menu Group */}
          {hasLoanAccess && (
            <div>
              <button
                onClick={() => toggleMenu('loan')}
                onMouseDown={(e) => e.preventDefault()}
                data-testid="nav-loan-management"
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  isLoanPath
                    ? "bg-forest-50 text-forest-900"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <div className="flex items-center gap-3">
                  <Banknote className="w-5 h-5" />
                  <span>Loan Management</span>
                </div>
                {loanMenuOpen ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              
              {/* Submenu Items */}
              {loanMenuOpen && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-forest-100 pl-3">
                  {filteredLoanItems.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setOpenMenu(null)}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                        location.pathname === item.href
                          ? "bg-forest-50 text-forest-900"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Other menu items */}
          <div>
            {filteredOtherItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  location.pathname === item.href
                    ? "bg-forest-50 text-forest-900"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            ))}
          </div>
          
          {/* Staff Management collapsible menu */}
          {hasStaffAccess && (
            <div>
              <button
                onClick={() => toggleMenu('staff')}
                onMouseDown={(e) => e.preventDefault()}
                data-testid="nav-staff-management"
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  isStaffPath
                    ? "bg-forest-50 text-forest-900"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <div className="flex items-center gap-3">
                  <UsersRound className="w-5 h-5" />
                  <span>Staff Management</span>
                </div>
                {staffMenuOpen ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              
              {/* Staff Submenu Items */}
              {staffMenuOpen && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-forest-100 pl-3">
                  {filteredStaffItems.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setOpenMenu(null)}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                        location.pathname === item.href
                          ? "bg-forest-50 text-forest-900"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Account Management collapsible menu */}
          {hasAccountAccess && (
            <div>
              <button
                onClick={() => toggleMenu('account')}
                onMouseDown={(e) => e.preventDefault()}
                data-testid="nav-account-management"
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  isAccountPath
                    ? "bg-forest-50 text-forest-900"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <div className="flex items-center gap-3">
                  <Wallet className="w-5 h-5" />
                  <span>Account Management</span>
                </div>
                {accountMenuOpen ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              
              {/* Account Submenu Items */}
              {accountMenuOpen && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-forest-100 pl-3">
                  {filteredAccountItems.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setOpenMenu(null)}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                        location.pathname === item.href
                          ? "bg-forest-50 text-forest-900"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* System Reports collapsible menu */}
          {hasReportsAccess && (
            <div>
              <button
                onClick={() => toggleMenu('reports')}
                onMouseDown={(e) => e.preventDefault()}
                data-testid="nav-system-reports"
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  isReportsPath
                    ? "bg-forest-50 text-forest-900"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <div className="flex items-center gap-3">
                  <FileBarChart className="w-5 h-5" />
                  <span>System Reports</span>
                </div>
                {reportsMenuOpen ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              
              {/* Reports Submenu Items */}
              {reportsMenuOpen && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-forest-100 pl-3">
                  {filteredReportsItems.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setOpenMenu(null)}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                        location.pathname === item.href
                          ? "bg-forest-50 text-forest-900"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* System Management collapsible menu */}
          {hasSystemAccess && (
            <div>
              <button
                onClick={() => toggleMenu('system')}
                onMouseDown={(e) => e.preventDefault()}
                data-testid="nav-system-management"
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  isSystemPath
                    ? "bg-forest-50 text-forest-900"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <div className="flex items-center gap-3">
                  <Cog className="w-5 h-5" />
                  <span>System Management</span>
                </div>
                {systemMenuOpen ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              
              {/* System Submenu Items */}
              {systemMenuOpen && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-forest-100 pl-3">
                  {filteredSystemItems.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setOpenMenu(null)}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                        location.pathname === item.href
                          ? "bg-forest-50 text-forest-900"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Investor Management collapsible menu */}
          {hasInvestorAccess && (
            <div>
              <button
                onClick={() => toggleMenu('investor')}
                onMouseDown={(e) => e.preventDefault()}
                data-testid="nav-investor-management"
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  isInvestorPath
                    ? "bg-forest-50 text-forest-900"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <div className="flex items-center gap-3">
                  <PiggyBank className="w-5 h-5" />
                  <span>Investor Management</span>
                </div>
                {investorMenuOpen ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              
              {/* Investor Submenu Items */}
              {investorMenuOpen && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-forest-100 pl-3">
                  {filteredInvestorItems.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setOpenMenu(null)}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                        location.pathname === item.href
                          ? "bg-forest-50 text-forest-900"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>
      </ScrollArea>
    </aside>
  );
};

export const CustomerSidebar = ({ className }) => {
  const location = useLocation();
  const { user } = useAuth();
  
  // Get unread chat count
  let unreadChatCount = 0;
  try {
    const chatContext = useChat();
    unreadChatCount = chatContext?.unreadTotal || 0;
  } catch (e) {
    // Chat context not available
  }

  // Detect investor context — show ONLY investor menu when on /investor/* routes
  // OR when user's only relevant role is 'investor' (no customer / loans).
  const userRoles = [user?.role, ...(user?.roles || [])].filter(Boolean);
  const isInvestorOnly = userRoles.includes('investor') && !userRoles.includes('customer');
  const onInvestorPath = location.pathname.startsWith('/investor');
  const showInvestorMenu = onInvestorPath || isInvestorOnly;

  const investorMenuItems = [
    { icon: LayoutDashboard, label: 'Dashboard',         href: '/investor/dashboard' },
    { icon: Wallet,          label: 'My Investments',    href: '/investor/investments' },
    { icon: TrendingUp,      label: 'Invest Now',        href: '/investor/products' },
    { icon: ArrowUpRight,    label: 'Withdrawals',       href: '/investor/withdrawals' },
    { icon: FileText,        label: 'Statement',         href: '/investor/statement' },
    { icon: UserPlus,        label: 'Refer an Investor', href: '/investor/refer' },
    { icon: MessageSquare,   label: 'Chat & Support',    href: '/customer/chat', badge: unreadChatCount },
    { icon: User,            label: 'Profile',           href: '/investor/profile' },
  ];

  const customerMenuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', href: '/customer/dashboard' },
    { icon: FileText, label: 'Apply for Loan', href: '/customer/apply' },
    { icon: Wallet, label: 'My Loans', href: '/customer/loans' },
    { icon: CreditCard, label: 'Payments', href: '/customer/payments' },
    { icon: Receipt, label: 'Transactions', href: '/customer/transactions' },
    { icon: Receipt, label: 'Fees Payment', href: '/customer/fees' },
    { icon: RefreshCcw, label: 'Refunds', href: '/customer/refunds' },
    { icon: MessageSquare, label: 'Chat & Support', href: '/customer/chat', badge: unreadChatCount },
    { icon: PiggyBank, label: 'Invest', href: '/investor/dashboard' },
    { icon: Building2, label: 'Update Information', href: '/customer/update-info' },
    { icon: BookOpen, label: 'Help & Manual', href: '/customer/manual' },
    { icon: User, label: 'Profile', href: '/customer/profile' },
  ];

  const menuItems = showInvestorMenu ? investorMenuItems : customerMenuItems;
  const portalLabel = showInvestorMenu ? 'Investor Portal' : 'Customer Portal';
  const homeHref = showInvestorMenu ? '/investor/dashboard' : '/customer/dashboard';

  return (
    <aside className={cn("w-64 bg-white border-r border-border flex flex-col", className)}>
      <div className="p-4 border-b border-border">
        <Link to={homeHref} className="flex items-center gap-2" data-testid="customer-sidebar-logo">
          <img src="/bbc-logo.png" alt="BBC Logo" className="w-9 h-9 rounded-lg object-cover" />
          <div>
            <span className="text-lg font-heading font-bold" style={{ color: 'var(--theme-primary)' }}>BBC</span>
            <span className="block text-xs text-muted-foreground">{portalLabel}</span>
          </div>
        </Link>
      </div>
      <ScrollArea className="flex-1 p-4">
        <nav className="space-y-1">
          {menuItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              data-testid={`customer-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                location.pathname === item.href
                  ? "bg-[var(--theme-highlight-bg)] text-[var(--theme-primary)]"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              <item.icon className="w-5 h-5" />
              <span className="flex-1">{item.label}</span>
              {item.badge > 0 && (
                <Badge className="bg-red-500 text-white text-xs px-1.5 py-0.5 min-w-[20px] text-center">
                  {item.badge > 99 ? '99+' : item.badge}
                </Badge>
              )}
            </Link>
          ))}
        </nav>
      </ScrollArea>
    </aside>
  );
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hook to manage mobile layout preference
const useMobileLayoutMode = () => {
  const [fullScreenMode, setFullScreenMode] = useState(() => {
    // Default to full-screen on mobile
    return localStorage.getItem('mobile_layout_mode') !== 'standard';
  });

  const toggleMode = () => {
    const newMode = !fullScreenMode;
    setFullScreenMode(newMode);
    localStorage.setItem('mobile_layout_mode', newMode ? 'fullscreen' : 'standard');
  };

  return { fullScreenMode, toggleMode };
};

// Social (chat/call) notifications are kept out of the in-app bell (push/WS/ringing untouched)
const isSocialNotif = (n) => {
  if (!n) return false;
  const cat = n?.metadata?.notification_category;
  return n.event_type === 'chat' || ['chat', 'call', 'calls', 'social'].includes(cat) || /^\/(staff|customer)\/chat/.test(n.link || '');
};

export const DashboardHeader = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Get registerHandler from chat context for real-time notification updates
  let registerHandler = null;
  try {
    const chatContext = useChat();
    registerHandler = chatContext?.registerHandler;
  } catch (e) {
    // Chat context not available
  }

  const fetchNotifications = useCallback(async (force = false) => {
    // The header remounts on every route change; reuse the last result for 25 s so menu changes feel instant.
    if (!force && window.__bbcNotifCache && Date.now() - window.__bbcNotifCache.at < 25000) {
      const c = window.__bbcNotifCache; setNotifications(c.list); setUnreadCount(c.unread); return;
    }
    try {
      // Share one in-flight request between the header instances mounted during the same load
      if (!window.__bbcNotifInflight) {
        window.__bbcNotifInflight = fetch(`${API}/notifications?unread_only=true`, { headers: getAuthHeaders(), credentials: 'include' })
          .finally(() => { window.__bbcNotifInflight = null; });
      }
      const response = await window.__bbcNotifInflight;
      if (response.ok) {
        response.__bbcData = response.__bbcData || response.json();
        const data = await response.__bbcData;
        // Hide chat notifications from the in-app bell — chat already has its
        // own unread badges per conversation, so showing them here is noise.
        const list = (data.notifications || []).filter((n) => !isSocialNotif(n));
        setNotifications(list);
        // Recompute unread_count from the filtered list so the badge stays
        // consistent with what's actually shown in the dropdown.
        // Server count already excludes social items (same filter), so use it directly
        const unread = typeof data.unread_count === 'number' ? data.unread_count : list.filter((n) => !n.read).length;
        setUnreadCount(unread);
        window.__bbcNotifCache = { at: Date.now(), list, unread };
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [getAuthHeaders]);

  // Listen for real-time notification updates via WebSocket
  useEffect(() => {
    if (registerHandler) {
      const unregister = registerHandler('newNotification', (data) => {
        // Social (chat + calls) notifications never enter the bell — they have their own surfaces.
        if (data?.bell_skip || isSocialNotif(data?.notification)) return;
        window.__bbcNotifCache = null;
        if (data.notification) {
          setNotifications(prev => [data.notification, ...prev.slice(0, 9)]);
        }
        if (data.unread_count !== undefined) {
          setUnreadCount(data.unread_count);
        }
      });
      return unregister;
    }
  }, [registerHandler]);

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds (only while the tab is visible)
    const interval = setInterval(() => { if (document.visibilityState === 'visible') fetchNotifications(); }, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleMarkRead = async (notificationId) => {
    window.__bbcNotifCache = null;
    try {
      // Remove the notification from the list immediately
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      await fetch(`${API}/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
    } catch (error) {
      console.error('Failed to mark as read:', error);
      // Revert on error
      fetchNotifications(true);
    }
  };

  const handleMarkAllRead = async () => {
    window.__bbcNotifCache = null;
    setLoading(true);
    try {
      // Clear all notifications from the list
      setNotifications([]);
      setUnreadCount(0);
      
      await fetch(`${API}/notifications/read-all`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      // Revert on error
      fetchNotifications(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClearAll = async () => {
    window.__bbcNotifCache = null;
    setLoading(true);
    try {
      // Optimistically update the UI
      setNotifications([]);
      setUnreadCount(0);
      
      await fetch(`${API}/notifications`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
    } catch (error) {
      console.error('Failed to clear notifications:', error);
      // Revert on error
      fetchNotifications(true);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      window.__bbcNotifCache = null;
      // Remove the notification from the list immediately
      setNotifications(prev => prev.filter(n => n.notification_id !== notification.notification_id));
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // Make the API call in background
      try {
        await fetch(`${API}/notifications/${notification.notification_id}/read`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          credentials: 'include'
        });
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }
    if (notification.link) {
      navigate(notification.link);
      setNotifOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Administrator',
      regional_manager: 'Regional Manager',
      branch_manager: 'Branch Manager',
      loan_officer: 'Loan Officer',
      cashier: 'Cashier',
      customer: 'Customer'
    };
    return labels[role] || role;
  };

  const getNotificationIcon = (type, metadata) => {
    // Check for chat/call notifications first
    const category = metadata?.notification_category;
    
    if (category === 'chat') {
      return <MessageSquare className="w-4 h-4 text-blue-500" />;
    }
    
    if (category === 'call') {
      const callStatus = metadata?.call_status;
      const callType = metadata?.call_type;
      
      if (callStatus === 'missed') {
        return <PhoneMissed className="w-4 h-4 text-red-500" />;
      }
      if (callType === 'video') {
        return <Video className="w-4 h-4 text-green-500" />;
      }
      return <Phone className="w-4 h-4 text-green-500" />;
    }
    
    // Default notification icons based on type
    switch(type) {
      case 'success': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'error': return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'warning': return <AlertCircle className="w-4 h-4 text-amber-500" />;
      default: return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  const location = useLocation();
  const isChatPage = location.pathname.includes('/chat');
  // Hide the Calendar icon for customer + investor portals — they don't
  // need it and the floating bubble was confusing on those screens.
  const isCustomerOrInvestor = (
    location.pathname.startsWith('/customer') ||
    location.pathname.startsWith('/investor')
  );

  return (
    <header
      className={cn(
        "h-16 bg-white border-b border-border flex items-center justify-between px-4 sm:px-6",
        isChatPage && "hidden md:flex"
      )}
      onClickCapture={() => markTabSwitchNav()}
    >
      <div className="flex items-center gap-4">
        <h1 className="text-lg font-semibold font-heading text-foreground" data-testid="dashboard-title">
          Welcome, {user?.name?.split(' ')[0] || 'User'}
        </h1>
        {!isCustomerOrInvestor && (
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('bbc:global-search-open'))}
            className="hidden md:flex items-center gap-2 h-9 w-64 lg:w-80 px-3 rounded-lg border border-border bg-muted/40 text-sm text-muted-foreground hover:bg-muted transition-colors"
            data-testid="global-search-trigger"
          >
            <Search className="w-4 h-4" />
            <span>Search anything…</span>
            <kbd className="ml-auto text-[10px] font-mono bg-background border border-border rounded px-1.5 py-0.5">⌘K</kbd>
          </button>
        )}
      </div>
      <div className="flex items-center gap-2">
        {/* AI Chatbot - desktop */}
        <AIChatbot />
        {/* Calendar - desktop (opens as floating bubble) — staff only */}
        {!isCustomerOrInvestor && (
          <Button
            variant="ghost"
            size="icon"
            data-testid="desktop-calendar-btn"
            data-floating-trigger="calendar"
            onClick={() => window.dispatchEvent(new CustomEvent('bbc:floating-toggle', { detail: { which: 'calendar' } }))}
          >
            <Calendar className="w-5 h-5 text-forest-700" />
          </Button>
        )}
        {/* Chat & Calls - desktop (opens as floating bubble) */}
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="desktop-chat-btn"
          data-floating-trigger="chat"
          onClick={() => window.dispatchEvent(new CustomEvent('bbc:floating-toggle', { detail: { which: 'chat' } }))}
        >
          <MessageSquare className="w-5 h-5 text-forest-700" />
        </Button>
        {/* Notification Bell */}
        <Popover open={notifOpen} onOpenChange={setNotifOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <Badge 
                  variant="destructive" 
                  className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 sm:w-96 p-0" data-testid="notifications-dropdown">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="font-semibold text-sm">Notifications</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleMarkAllRead} 
                    disabled={loading}
                    className="text-xs h-7"
                    data-testid="mark-all-read-btn"
                  >
                    <CheckCheck className="w-3 h-3 mr-1" />
                    Mark all read
                  </Button>
                )}
                {notifications.length > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleClearAll} 
                    disabled={loading}
                    className="text-xs h-7 text-muted-foreground"
                    data-testid="clear-all-btn"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
            <ScrollArea className="h-[300px]">
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                  <Bell className="w-10 h-10 mb-2 opacity-30" />
                  <p className="text-sm">No notifications</p>
                </div>
              ) : (
                <div className="divide-y">
                  {notifications.map((notification) => (
                    <div
                      key={notification.notification_id}
                      className={cn(
                        "px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors",
                        !notification.read && "bg-forest-50/50"
                      )}
                      onClick={() => handleNotificationClick(notification)}
                      data-testid={`notification-${notification.notification_id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          {getNotificationIcon(notification.type, notification.metadata)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={cn(
                              "text-sm truncate",
                              !notification.read && "font-semibold"
                            )}>
                              {notification.title}
                            </p>
                            {!notification.read && (
                              <span className="w-2 h-2 bg-forest-500 rounded-full flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDate(notification.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
            <Separator />
            <div className="p-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-xs h-8"
                onClick={() => {
                  navigate('/staff/notification-settings');
                  setNotifOpen(false);
                }}
                data-testid="notification-settings-btn"
              >
                <Settings className="w-3 h-3 mr-2" />
                Notification Settings
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-forest-100 text-forest-900 text-sm">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="hidden md:block text-left">
                <div className="text-sm font-medium">{user?.name}</div>
                <div className="text-xs text-muted-foreground">{getRoleLabel(user?.role)}</div>
              </div>
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to={(() => {
                // Investor portal: Profile shows a read-only investor info page.
                if (location.pathname.startsWith('/investor')) return '/investor/profile';
                if (user?.role === 'customer') return '/customer/profile';
                return '/staff/profile';
              })()} data-testid="profile-link">
                <User className="mr-2 h-4 w-4" />
                Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={(() => {
                // Investor portal: Settings route → dashboard (investors don't
                // have meaningful settings; sending them to dashboard avoids a
                // dead-end page).
                if (location.pathname.startsWith('/investor')) return '/investor/dashboard';
                if (user?.role === 'customer') return '/customer/settings';
                return '/staff/settings';
              })()} data-testid="settings-link">
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5">
              <BuildVersionBadge />
            </div>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5">
              <AppVersionInfo />
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

// True per-tab freeze (StaffTabKeepAliveShell) renders every open tab's page
// simultaneously — StaffLayoutChromeImpl below is the actual chrome markup,
// mounted exactly once by the shell. Individual pages still call the plain
// `StaffLayout` export (unchanged, 70+ files); it becomes a no-op passthrough
// whenever it detects it's already running inside that persistent shell, so
// the sidebar/header/tab-bar never gets duplicated once per open tab.
export const StaffShellContext = createContext(false);

export const StaffLayout = ({ children }) => {
  const insideShell = useContext(StaffShellContext);
  if (insideShell) return children;
  return <StaffLayoutChromeImpl>{children}</StaffLayoutChromeImpl>;
};

export const StaffLayoutChromeImpl = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const mobileHeaderRef = useRef(null);
  useStickyHeaderVar(mobileHeaderRef, '--bbc-mobile-header-h');
  const { maximized } = useTabs();
  const { fullScreenMode } = useMobileLayoutMode();
  const { user, token, workLocked: workAreaLocked } = useAuth();
  const { unreadTotal } = useChat();
  const location = useLocation();
  const isChatPage = location.pathname.includes('/chat');

  // Escape closes the mobile drawer — otherwise the backdrop is left up
  // and intercepts clicks on header/tab-bar controls behind it.
  useEffect(() => {
    if (!sidebarOpen) return;
    const onKeyDown = (e) => { if (e.key === 'Escape') setSidebarOpen(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [sidebarOpen]);

  // Auto-close the mobile drawer on navigation (same UX as the customer/
  // investor portal) so tapping a menu item lands on the page directly.
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);
  useEffect(() => { const open = () => setSidebarOpen(true); window.addEventListener('bbc:open-sidebar', open); return () => window.removeEventListener('bbc:open-sidebar', open); }, []);

  // Register FCM token for native push notifications
  useEffect(() => {
    if (!token) return;
    const isNative = navigator.userAgent.includes('BBCCreditsApp');
    if (!isNative) return;

    let attempts = 0;
    const maxAttempts = 15;
    let registered = false;

    const tryRegister = async () => {
      if (registered) return;
      let fcmToken = null;

      // Try to get token from bridge or global
      if (window.__FCM_TOKEN__ && window.__FCM_TOKEN__.length > 10) {
        fcmToken = window.__FCM_TOKEN__;
      } else if (window.AndroidBridge && typeof window.AndroidBridge.getFcmToken === 'function') {
        try { fcmToken = window.AndroidBridge.getFcmToken(); } catch(e) {}
      }

      if (fcmToken && fcmToken.length > 10) {
        try {
          const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/fcm/register`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: fcmToken, device_name: (() => { try { return window.AndroidBridge.getDeviceName(); } catch { return 'Android Device'; } })(), device_id: (() => { try { return window.AndroidBridge.getDeviceId(); } catch { return localStorage.getItem('bbc_device_id'); } })(), app_version: (() => { try { return Number(window.AndroidBridge.getVersionCode()) || null; } catch { return null; } })() }),
          });
          if (resp.ok) { registered = true; return; }
        } catch(e) { /* silent */ }
      }
      
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(tryRegister, 3000);
      }
    };

    // Listen for token callback from native
    window.__onFcmToken__ = (t) => {
      if (t && t.length > 10 && !registered) {
        window.__FCM_TOKEN__ = t;
        tryRegister();
      }
    };

    setTimeout(tryRegister, 1000);
    return () => { delete window.__onFcmToken__; };
  }, [token]);

  const socialPathsForLock = ['/staff/calendar', '/staff/chat', '/staff/support-queue', '/staff/social-contacts', '/staff/navigation'];
  const lockPath = (location).pathname;
  const showWorkLock = !!workAreaLocked && !socialPathsForLock.includes(lockPath) && !lockPath.startsWith('/staff/social');
  return (
    <div className="min-h-screen bg-background flex" style={{ overflowX: 'clip' }}>
      {showWorkLock && <WorkAreaLock />}
      {/* Mobile sidebar backdrop (also shown on desktop while maximised, since
          the sidebar becomes an overlay drawer in that mode) */}
      {sidebarOpen && (
        <div 
          className={cn("fixed inset-0 bg-black/50 z-40", !maximized && "lg:hidden")}
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar — normally always visible on desktop (lg:flex); while a tab
          is maximised (focus mode) it becomes an on-demand overlay drawer on
          ALL screen sizes so side-menu pages stay reachable without leaving
          focus mode. */}
      <StaffSidebar className={cn(
        "fixed inset-y-0 left-0 z-50",
        maximized
          ? (sidebarOpen ? "flex" : "hidden")
          : cn("lg:static lg:flex", sidebarOpen ? "flex" : "hidden lg:flex")
      )} />

      {/* Main content */}
      <div className={cn(
        "flex-1 flex flex-col min-w-0",
        isChatPage ? "h-[100dvh] max-h-[100dvh]" : "min-h-screen"
      )} style={{ overflowX: 'clip' }}>
        {/* Floating hamburger menu button — only needed in maximised/focus
            mode, where the normal sidebar AND the mobile sticky header
            (which has its own hamburger button) are both hidden. Showing it
            in normal mode too used to duplicate/overlay the mobile header's
            own button and swallow its clicks. */}
        {!sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(true)}
            className={cn(
              "fixed top-3 left-3 z-50 w-10 h-10 flex items-center justify-center text-foreground",
              maximized ? "flex" : "hidden"
            )}
            data-testid="floating-menu-btn"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        )}

        {/* Mobile header bar */}
        {!maximized && (
        <div
          ref={mobileHeaderRef}
          className="lg:hidden sticky top-0 z-30 p-3 border-b border-border bg-white flex items-center justify-between shrink-0"
          onClickCapture={() => markTabSwitchNav()}
        >
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
              <Menu className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              data-testid="mobile-chat-btn"
              data-floating-trigger="chat"
              onClick={() => window.dispatchEvent(new CustomEvent('bbc:floating-toggle', { detail: { which: 'chat' } }))}
            >
              <MessageSquare className="w-5 h-5 text-forest-700" />
              {unreadTotal > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                  {unreadTotal > 9 ? '9+' : unreadTotal}
                </span>
              )}
            </Button>
          </div>
          <Link to="/staff/dashboard" className="flex items-center gap-2">
            <img src="/bbc-logo.png" alt="BBC Logo" className="w-8 h-8 rounded-lg object-cover" />
            <span className="font-heading font-bold text-forest-800">BBC</span>
          </Link>
          <div className="flex items-center gap-1">
            {/* Global search */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => window.dispatchEvent(new CustomEvent('bbc:global-search-open'))}
              data-testid="mobile-global-search-btn"
              aria-label="Search"
            >
              <Search className="w-5 h-5 text-forest-700" />
            </Button>
            {/* AI Chatbot */}
            <AIChatbot />
            {/* Calendar icon — opens as floating bubble on mobile */}
            <Button
              variant="ghost"
              size="icon"
              data-testid="mobile-calendar-btn"
              data-floating-trigger="calendar"
              onClick={() => window.dispatchEvent(new CustomEvent('bbc:floating-toggle', { detail: { which: 'calendar' } }))}
            >
              <Calendar className="w-5 h-5 text-forest-700" />
            </Button>
          </div>
        </div>
        )}
        <OfflineBanner />
        <MobileBalanceGate />
        {!maximized && <DashboardHeader />}
        <HeaderTabsBar />
        <main className={cn(
          "flex-1 min-h-0 min-w-0",
          isChatPage ? "p-0 overflow-hidden" : "layout-main-content " + (fullScreenMode ? "p-2 sm:p-4 md:p-6" : "p-0 sm:p-4 md:p-6")
        )}>
          {!isChatPage && <AutoBreadcrumb className="mb-4 px-2 sm:px-0" />}
          {children}
        </main>
        {!isChatPage && (
          <footer className="hidden lg:flex items-center justify-center py-1 text-[10px] text-muted-foreground/50 border-t border-border/30">
            v3.0.0
          </footer>
        )}
        <ScrollToTopButton />
        <GlobalCallOverlay />
      </div>
      <GlobalSearch />
    </div>
  );
};

export const CustomerLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { fullScreenMode, toggleMode } = useMobileLayoutMode();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const location = useLocation();
  const isChatPage = location.pathname.includes('/chat');

  // Auto-close the mobile drawer whenever the route changes (tapping a menu
  // item should take the user straight to the page, not leave the drawer +
  // backdrop covering it).
  const { workLocked: workAreaLocked } = useAuth();
  const socialPaths = ['/staff/calendar', '/staff/chat', '/staff/support-queue', '/staff/social-contacts', '/staff/navigation'];
  const showWorkLock = !!workAreaLocked && !socialPaths.includes(location.pathname) && !location.pathname.startsWith('/staff/social');
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background flex" style={{ overflowX: 'clip' }}>
      {showWorkLock && <WorkAreaLock />}
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar - Hidden on mobile in full-screen mode */}
      <CustomerSidebar className={cn(
        "fixed inset-y-0 left-0 z-50 lg:static lg:flex",
        fullScreenMode 
          ? (sidebarOpen ? "flex" : "hidden lg:flex")
          : (sidebarOpen ? "flex" : "hidden lg:flex")
      )} />

      {/* Main content */}
      <div className={cn(
        "flex-1 flex flex-col min-w-0",
        isChatPage ? "h-screen max-h-screen" : "min-h-screen"
      )} style={{ overflowX: 'clip' }}>
        {/* Floating hamburger menu button - always visible on mobile, hides when sidebar open */}
        {!sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden fixed top-3 left-3 z-50 w-10 h-10 flex items-center justify-center text-foreground"
            data-testid="floating-customer-menu-btn"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        )}

        <div className="lg:hidden sticky top-0 z-30 p-3 border-b border-border bg-white flex items-center justify-between shrink-0">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)} data-testid="customer-mobile-menu-btn">
            <Menu className="w-5 h-5" />
          </Button>
          <Link to="/customer/dashboard" className="flex items-center gap-2">
            <img src="/bbc-logo.png" alt="BBC Logo" className="w-8 h-8 rounded-lg object-cover" />
            <span className="font-heading font-bold text-[var(--theme-primary)]">BBC</span>
          </Link>
          {/* Right side: Chat button + Admin settings */}
          <div className="flex items-center gap-1">
            {/* Chat/Support button in header */}
            <Link to="/customer/chat" data-testid="header-chat-btn">
              <Button variant="ghost" size="icon" className="text-[var(--theme-primary)]">
                <MessageSquare className="w-5 h-5" />
              </Button>
            </Link>
            {/* Admin toggle for mobile layout mode */}
            {isAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="customer-mobile-layout-toggle">
                    <Settings className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel className="text-xs">Mobile Layout</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={toggleMode}
                    className="text-xs cursor-pointer"
                  >
                    {fullScreenMode ? (
                      <>
                        <Menu className="w-4 h-4 mr-2" />
                        Switch to Standard
                      </>
                    ) : (
                      <>
                        <Leaf className="w-4 h-4 mr-2" />
                        Switch to Full Screen
                      </>
                    )}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        <OfflineBanner />
        <DashboardHeader />

        <main className={cn(
          "flex-1 min-h-0 min-w-0",
          isChatPage ? "p-0 overflow-hidden" : "pb-20 lg:pb-6 layout-main-content " + (fullScreenMode ? "p-2 sm:p-4 md:p-6" : "p-0 sm:p-4 md:p-6")
        )}>
          {!isChatPage && <AutoBreadcrumb className="mb-4 px-2 sm:px-0" />}
          {children}
        </main>
        <ScrollToTopButton />
        <GlobalCallOverlay />
      </div>
    </div>
  );
};
